import React from 'react';
import { SelectValue } from 'antd/lib/select';
import { useTranslation } from 'react-i18next';

import {
  CustomOptionsStatus,
  ExternalMaskingDetectorSettingsInput,
  ExternalMaskingErrorPolicy,
  ExternalReplacementMode,
  GuardrailSettingsState,
} from '../../../../../gamma-api';
import IbButton from '../../../../components/common/IbButton';
import { CodeEditorStatus } from '../../../../components/common/CodeEditor/types';
import HttpHeadersEditor from '../../../../components/common/HttpHeadersEditor';
import IbIcon from '../../../../components/common/IbIcon';
import IbInput from '../../../../components/common/IbInput';
import IbNumberInput from '../../../../components/common/IbNumberInput';
import IbSelect from '../../../../components/common/IbSelect';
import IbSwitch from '../../../../components/common/IbSwitch';
import IbTypography from '../../../../components/common/IbTypography';
import JsonEditorExpandable from '../../../../components/common/JsonEditorExpandable';
import { HTTP_HEADERS_CLASS_NAME, ITEM_CLASS_NAME } from '../../../../components/common/TypedStepModal/const';
import { UseValidationForm } from '../../../../utils/validationUtil';
import { getGuardrailErrorPolicyLabel, GuardrailDetectorSettings, IMaskingDetectorStep } from '../shared/utils';

import { DetectorValidationField, DetectorValidationForm } from './DetectorModal.validation';
import CommonDetectorParameters from './CommonDetectorParameters';

export interface IExternalDetectorParametersProps {
  step: IMaskingDetectorStep;
  validationFieldRefs: Partial<Record<DetectorValidationField, React.RefObject<HTMLDivElement>>>;
  validationForm: UseValidationForm<DetectorValidationForm>;
  onChange: (patch: Partial<GuardrailDetectorSettings>) => void;
  onDownloadSchema: () => void;
}

const ExternalDetectorParameters: React.FC<IExternalDetectorParametersProps> = ({
  step,
  validationFieldRefs,
  validationForm,
  onChange,
  onDownloadSchema,
}) => {
  const { t } = useTranslation();
  const detector = step.detector as ExternalMaskingDetectorSettingsInput;
  const customOptionsEnabled = detector.custom_options?.status === CustomOptionsStatus.Enabled;
  const { validationState, onFieldChange, onFieldBlur, getShowError, getErrorMessage } = validationForm;
  const replacementModeOptions = [
    ExternalReplacementMode.PipelineGenerated,
    ExternalReplacementMode.UseExternalReplacement,
  ].map((value) => ({
    value,
    label: t(`Agent: Guardrails: Masking: External: ReplacementMode: ${value}`),
  }));
  const errorPolicyOptions = [ExternalMaskingErrorPolicy.FailClosed, ExternalMaskingErrorPolicy.FailOpen].map(
    (value) => ({
      value,
      label: getGuardrailErrorPolicyLabel(value),
    })
  );

  const onStateChange = (state: GuardrailSettingsState) => {
    onChange({ state });
    onFieldChange('state', state);
  };

  const onApiUrlChange = (value: string) => {
    onChange({ api_url: value });
    onFieldChange('apiUrl', value);
  };

  const onCustomOptionsStatusChange = (checked: boolean) => {
    const status = checked ? CustomOptionsStatus.Enabled : CustomOptionsStatus.Disabled;
    onChange({
      custom_options: {
        ...detector.custom_options,
        status,
      },
    });
    onFieldChange('customOptionsStatus', status);
  };

  const onCustomOptionsJsonChange = (value?: string) => {
    const jsonValue = value ?? '';
    onChange({
      custom_options: {
        ...detector.custom_options,
        json_value: jsonValue,
      },
    });
    onFieldChange('customOptionsJson', jsonValue);
  };

  const onApiUrlBlur = () => {
    onFieldBlur('apiUrl');
  };

  const onRequestHeadersChange = (value: Record<string, string>) => {
    onChange({ request_headers: value });
  };

  const onTimeoutChange = (value: string | number) => {
    onChange({ timeout_seconds: value as number });
  };

  const onMaxRetriesChange = (value: string | number) => {
    onChange({ max_retries: value as number });
  };

  const onErrorPolicyChange = (value: SelectValue) => {
    onChange({ on_error: value as ExternalMaskingErrorPolicy });
  };

  const onReplacementModeChange = (value: SelectValue) => {
    onChange({ replacement_mode: value as ExternalReplacementMode });
  };

  return (
    <>
      <div className={ITEM_CLASS_NAME}>
        <IbTypography.Paragraph type="secondary">
          {t('Agent: Guardrails: Masking: Detector: External: Description')}
        </IbTypography.Paragraph>
        <IbButton icon={<IbIcon iconName="download" />} type="link" onClick={onDownloadSchema}>
          {t('Agent: Guardrails: Webhook: DownloadSchema')}
        </IbButton>
      </div>
      <CommonDetectorParameters detector={step.detector} onChange={onChange} onStateChange={onStateChange} />
      <div ref={validationFieldRefs.apiUrl} className={ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Guardrails: Webhook: URL: Title')}
        </IbTypography.Paragraph>
        <IbTypography.Paragraph type="secondary">
          {t('Agent: Guardrails: Webhook: URL: Description')}
        </IbTypography.Paragraph>
        <IbInput
          placeholder="https://masking.example.com/anonymize"
          status={getShowError(validationState.apiUrl) ? 'error' : 'default'}
          value={detector.api_url ?? ''}
          onBlur={onApiUrlBlur}
          onChange={onApiUrlChange}
        />
        {getShowError(validationState.apiUrl) && (
          <IbTypography.Paragraph error type="descriptor">
            {getErrorMessage(validationState.apiUrl, t('Agent: Guardrails: ExternalCheck: URL: Error'))}
          </IbTypography.Paragraph>
        )}
      </div>
      <div className={ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Guardrails: Webhook: RequestHeaders: Title')}
        </IbTypography.Paragraph>
        <HttpHeadersEditor
          className={HTTP_HEADERS_CLASS_NAME}
          value={detector.request_headers ?? {}}
          onChange={onRequestHeadersChange}
        />
      </div>
      <div className={ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Guardrails: Masking: External: Timeout: Title')}
        </IbTypography.Paragraph>
        <IbNumberInput min={1} value={detector.timeout_seconds} onChange={onTimeoutChange} />
      </div>
      <div className={ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Guardrails: Masking: External: MaxRetries: Title')}
        </IbTypography.Paragraph>
        <IbNumberInput max={5} min={0} value={detector.max_retries} onChange={onMaxRetriesChange} />
      </div>
      <div className={ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Guardrails: Masking: External: OnError: Title')}
        </IbTypography.Paragraph>
        <IbSelect options={errorPolicyOptions} value={detector.on_error} onChange={onErrorPolicyChange} />
      </div>
      <div className={ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Agent: Guardrails: Masking: External: ReplacementMode: Title')}
        </IbTypography.Paragraph>
        <IbSelect
          options={replacementModeOptions}
          value={detector.replacement_mode}
          onChange={onReplacementModeChange}
        />
      </div>
      <div ref={validationFieldRefs.customOptionsJson} className={ITEM_CLASS_NAME}>
        <IbTypography>
          <IbSwitch checked={customOptionsEnabled} size="small" onChange={onCustomOptionsStatusChange} />
          <IbTypography.Paragraph strong type="secondary">
            {t('Agent: Guardrails: Webhook: CustomOptions: Title')}
          </IbTypography.Paragraph>
        </IbTypography>
        <IbTypography.Paragraph type="secondary">
          {t('Agent: Guardrails: Webhook: CustomOptions: Description')}
        </IbTypography.Paragraph>
        <JsonEditorExpandable
          readOnly={!customOptionsEnabled}
          schema={{}}
          status={getShowError(validationState.customOptionsJson) ? CodeEditorStatus.Error : CodeEditorStatus.Default}
          value={detector.custom_options?.json_value ?? ''}
          onChange={onCustomOptionsJsonChange}
        />
        {getShowError(validationState.customOptionsJson) && (
          <IbTypography.Paragraph error type="descriptor">
            {getErrorMessage(validationState.customOptionsJson, t('Agent: Guardrails: Webhook: CustomOptions: Error'))}
          </IbTypography.Paragraph>
        )}
      </div>
    </>
  );
};

export default ExternalDetectorParameters;
